// Expenses & Other Revenue.
//
// MMS puts both on one screen for a reason: a teacher's question is "what did I
// make this month", and that needs teaching income, non-teaching income and
// costs in the same view. Net Income is the number they came for.
//
// Amounts are integer minor units, like everything else that touches money.

// ── Adapters ───────────────────────────────────────────────────────────────
//
// The rules are `core/src/expenses.rs`, so the Android and iOS expense screens
// fold the same entries into the same months, refuse the same category names,
// write the same documents and keep the same receipts as these. What stays
// here is the coercion — a document's loose values into the core's typed
// records, and the core's rows back into a document — the constants a module
// reads at load (held to the core's by `test/core/expenses.wasm.test.js`),
// `entriesBetween` (the reports' plain date filter over the ledger), and
// `planExpenseAttachmentMigration`, which only the one-off migration script
// runs. `core/src/expenses_vectors.json` holds the recorded answers.

import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { isActive, tombstoneOf } from './tombstone.js';

const text = (v) => (v == null ? null : String(v));
const str = (v) => (typeof v === 'string' ? v : null);
// `numOrNull`, as recurrence.js reads a rule's scalars: absent, null, `''`
// and anything not finite are none.
const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** An entry as the core's folds read it. */
export function entryOf(e) {
  const { voided, voidedAt } = tombstoneOf(e);
  return {
    id: String(e?.id ?? ''),
    entryType: String(e?.type ?? ''),
    payee: text(e?.payee),
    category: text(e?.category),
    amountMinor: Number(e?.amountMinor),
    isRefund: Boolean(e?.isRefund),
    occurredOn: text(e?.occurredOn),
    recurrence: text(e?.recurrence),
    frequency: text(e?.frequency),
    interval: numOrNull(e?.interval),
    endDate: text(e?.endDate),
    byWeekday: numOrNull(e?.byWeekday),
    weekOfMonth: numOrNull(e?.weekOfMonth),
    voided,
    voidedAt: voidedAt || null,
    // Revenue & Expenses' itemized lines carry it (#503); no fold reads it.
    description: text(e?.description),
  };
}

const ledgerOf = (l) => {
  const { voided, voidedAt } = tombstoneOf(l);
  return {
    kind: String(l?.kind ?? ''),
    amountMinor: Number(l?.amountMinor),
    occurredOn: text(l?.occurredOn),
    voided,
    voidedAt: voidedAt || null,
  };
};

/** A receipt — a row, an inline item or the form's — as the core reads it. */
const attachmentIn = (a) => ({
  id: text(a?.id),
  entryId: str(a?.entryId),
  path: str(a?.path),
  name: a?.name ? String(a.name) : null,
  contentType: str(a?.contentType),
  size: a?.size == null ? null : Number(a.size),
  addedAt: str(a?.addedAt),
});

// An entry's two inline shapes: the list with its holes dropped (whenever
// there is one), and the singular field.
const inlineOf = (entry) => {
  const hasList = Array.isArray(entry?.attachments);
  const list = hasList ? entry.attachments.filter(Boolean) : [];
  const single = entry?.attachment || null;
  return { hasList, list, single };
};

const pickedFrom = (rows, { list, single }) => (p) => {
  if (p.source === 'row') return rows[p.index];
  if (p.source === 'list') return list[p.index];
  return single;
};

// The core's row back as the six fields the document holds.
const attachmentOut = ({ entryId, path, name, contentType, size, addedAt }) => (
  { entryId, path, name, contentType, size, addedAt }
);

export const ENTRY_TYPES = {
  expense: { id: 'expense', label: 'Expense' },
  revenue: { id: 'revenue', label: 'Other Revenue' },
};

// Starting categories, offered when the studio has not defined its own. Chosen
// to cover what a private music teacher actually deducts.
export const DEFAULT_EXPENSE_CATEGORIES = [
  'Bank Fees', 'Equipment', 'Memberships and Dues', 'Other',
  'Professional Fees', 'Rent', 'Teaching Supplies', 'Travel',
];

export const DEFAULT_REVENUE_CATEGORIES = [
  'Gig', 'Other', 'Payments from Families',
];

// ── "Not Specified" is a placeholder, and the form will not save it (#116) ──
//
// MMS enables Save on an amount alone, but clicking it saves nothing and marks
// the field: "Not Specified" is the placeholder in an empty Category select,
// none of the dropdown's categories is that one, and the submit is refused.
// It is the *absence* of a category.
//
// Stored as empty string, so a renamed placeholder does not rewrite historical
// rows and the form can refuse it without any storage change.
export const CATEGORY_UNSET = '';

// **Still a label on the reading side**, because the unset state is reachable
// by one route (#134): **deleting a category** writes `category: ''` onto
// every entry that used it (`removeCategory` in src/data/useExpenses.jsx,
// which the confirm dialog says), rather than losing the rows or blocking the
// delete. The CSV import does not produce it: it refuses a row whose category
// the studio does not have (`resolveImportCategory` in
// src/domain/importDelimited.js). So the lists and the breakdowns render this
// label for rows that carry the state; neither way a teacher *creates* an
// entry produces it.
export const CATEGORY_UNSET_LABEL = 'Not Specified';

// Categories start as constants and become real records the first time the
// studio opens the category manager — MMS lists all eleven as editable rows, so
// renaming or deleting one has to be expressible, and a constant cannot be.
//
// Which mode we are in is decided by an explicit marker document rather than by
// "are there any docs yet". Inferring it would make deleting the last category
// silently resurrect all eleven.
export const CATEGORY_SEED_MARKER = '_seeded';

// ── How long the free text on an expense may be (#145) ──────────────────────
//
// `match /users/{uid}/expenseEntries/{id}` bounds `payee`, `category`,
// `description` and the recurrence group, as #121 does the student document:
// `useExpenses` holds every entry of a studio in one snapshot and folds them
// for the summary, the trend chart and the breakdown, so a megabyte in one
// `description` would be paid on every page load.
//
// **A number per field** (#121):
//
//   `payee` **200** — a person's or a vendor's name, so the number `firstName`,
//     `lastName`, `school` and `referrer` already carry. Written by the form,
//     by the CSV import, and by the receipt scanner.
//
//   `category` **100** — **fixed by the other collection.** `expenseCategories`
//     requires `0 < label.size() < 100`, and `renameCategory` copies that
//     label onto every entry filed under it; a bound here that disagreed would
//     make a rename half-apply, as #121 says of `categoryLabel` on a student.
//
//   `description` **5000** — the number `publicDescription` and
//     `privateDescription` on an event series carry: a few lines under a
//     three-row textarea. Not the student note's 65535, which is that large
//     because a *refused* student document takes the family account down with
//     it in the same batch (#115); nothing here is batched with another
//     collection.
//
// The constants below stop one short of each rules bound, as `STUDENT_NOTE_MAX`
// and `STUDENT_TAG_CHARS_MAX` do: the rule is written `size() < N`, so the
// longest value it accepts is N - 1 and the client's `maxLength` stops there.
//
// ── The recurrence group gets shapes, not lengths ───────────────────────────
//
// `recurrence`, `frequency`, `interval` and `endDate` are read by `expandEntry`
// and decide how many instances a rule produces. They are bounded in
// firestore.rules as a closed set, a closed set, a positive integer and a date
// — see the clauses there.
//
// ── The writers ─────────────────────────────────────────────────────────────
//
//   * `addEntry` / `updateEntry` in data/useExpenses.jsx, from the add/edit
//     drawer — `payee` a text input, `description` a textarea, `category` a
//     select over the studio's own list, and the four recurrence fields from
//     RecurrenceFields.
//   * `importEntries`, from the CSV wizard. `payee` and `description` come
//     straight off the spreadsheet, and the import writes in batches of 400,
//     so one long cell would take the batch down; `buildImportRows` refuses
//     those rows in the preview instead.
//   * `scanReceipt` in hooks/useExpenseEntryForm.js, which fills `payee` and
//     `category` from an OCR service's reply. A `maxLength` does not constrain
//     a programmatic fill, so `scanned_entry_form` in core/src/expenses.rs
//     clamps them.
//
// `byWeekday` and `weekOfMonth` are **not** bounded: `expandEntry` reads them,
// and no writer produces them (the expense form has no nth-weekday anchor,
// #121).
export const EXPENSE_PAYEE_MAX = 199;
export const EXPENSE_CATEGORY_MAX = 99;
export const EXPENSE_DESCRIPTION_MAX = 4999;

// ── An expense takes many attachments (#104), and the number is ours (#183) ──
//
// MMS stores several files on one expense, each listed as its own card under
// the paperclip, so the field is plural.
//
// ── Why the number is a constant and not a rules bound ─────────────────────
//
// `firestore.rules` cannot iterate a list, so a list of maps on the entry
// would be validated by naming every slot, and Firestore caps one rules
// evaluation at 1,000 expressions charged for `create` and `update` together:
// three attachments fit and four are refused with "maximum of 1000
// expressions to evaluate has been reached". So an attachment is its own
// document in `expenseAttachments` (#183), with its own rules evaluation, and
// the count is not expressed in the rule at all.
//
// **Ten** (#104): the evidence for one expense is a handful of files — a till
// receipt, the card statement page, an emailed invoice, a warranty. Not
// "unlimited", for two reasons:
//
//   * **The bytes.** Every receipt is charged against the studio's storage
//     allowance (src/lib/fileLimits.js).
//   * **The window.** `useExpenses` holds a studio's attachments in one
//     snapshot, as it holds its entries, so slack here is paid on every page
//     load. `attachmentsFor` filters that one window rather than querying per
//     entry.
//
// `ATTACHMENTS_FULL` in src/domain/expenseForms.js is the sentence a teacher
// meets at the eleventh file.
//
// MMS states no cap in its Add Expense drawer and stages dozens of files
// without complaint (#183); whether its server caps them is not known. This
// imposes a limit where MMS appears not to, and says it in the drawer.
export const EXPENSE_ATTACHMENTS_MAX = 10;

// The teacher's own filename, kept for display. `uploadAttachment` writes
// whatever `file.name` held, so the rule bounds it beside `attachment.path`
// (#104): a picker does not produce a 200 kB filename, but a caller can write
// one, and the studio would pay for it on every page load (#145). Under 300 to
// match `name` on a files/ row, the identical field on the identical kind of
// object.
export const EXPENSE_ATTACHMENT_NAME_MAX = 299;

// ── Where a receipt is stored, in three shapes rather than two (#183) ───────
//
// A row can name its receipts in three ways, and every reader here copes with
// all of them for entries `scripts/migrate-expense-attachments.mjs` has not
// reached:
//
//   1. **`expenseAttachments` rows**, one per file, which is what this app
//      writes. `attachmentsForEntry` below is the reader.
//   2. **`attachments`**, a list on the entry document (#104).
//   3. **`attachment`**, one object or null, on rows written before #104.
//
// 2 and 3 are `inlineAttachmentsOf` below. Nothing in `src/data/` writes
// either; the migration moves them into 1, and until it has, the cards render
// and the objects are swept. A reader that knew only one of the three would
// call a teacher's tax evidence an orphan (#140).

// The attachments on one entry, in the order the drawer and the row draw them.
//
// **Sorted by `addedAt` and then by id**: the order they were attached.
// `addedAt` is a client-written ISO instant rather than a `serverTimestamp()`,
// which reads as `null` in the local snapshot until the write is acknowledged
// (never, offline), so sorting on one would shuffle the cards when an ack
// landed. The id breaks a tie, so the order is total and stable.
export function attachmentsForEntry(rows = [], entryId) {
  if (!entryId) return [];
  return Array.from(
    coreSync().expenseAttachmentsForEntry(rows.map(attachmentIn), String(entryId)),
    (i) => rows[i],
  );
}

/// The document id for a receipt, derived from the object it points at.
///
/// **Derived rather than `freeId('att')`, because two rows for one object is a
/// defect rather than two receipts** — `core/src/ids.rs`' rule for when an id
/// must be a function of the facts.
///
/// `updateEntry` in src/data/useExpenses.jsx reconciles the form's list against
/// the rows the window is showing. With a random id, two saves in quick
/// succession could see no rows yet and create a second row for every path,
/// with a delete on either card taking the one object both point at. Derived,
/// the second create is a write of the same content to the same path, and a
/// replayed offline create is idempotent.
///
/// **The object's own filename is the whole of the id**: `receiptFilename` in
/// functions/files/paths.js mints `cap + ext` where `cap` is base64url, unique
/// per upload and safe as a document id. The prefix keeps the id from ever
/// being `.` or `..`, the two strings Firestore refuses outright.
///
/// The sanitise and the clamp are for a path this app did not mint; a `slice`
/// cannot make two distinct filenames collide unless they already share a
/// 200-character prefix.
export function attachmentIdForPath(path) {
  return coreSync().expenseAttachmentIdForPath(String(path ?? ''));
}

/// What a whole attachment document looks like.
///
/// The four fields `uploadAttachment` returns, plus the `entryId` that says
/// which expense it is on and the `addedAt` the order comes from. `entryId` is
/// normalised here rather than merged in by the caller, as
/// `normaliseFamilyContact` does `familyId`: a row with no entry is one no
/// screen can reach.
export function normaliseExpenseAttachment(input = {}, now = new Date().toISOString()) {
  return attachmentOut(coreSync().expenseNormaliseAttachment(attachmentIn(input), String(now)));
}

// Every receipt an entry names **on its own document**, from either inline
// shape — the list (#104) or the single object on rows written before it.
//
// Not `attachmentsOf` (#183): it reads an entry's attachments that have not
// been migrated. The live answer is `attachmentsForEntry` above and the
// `expenseAttachments` window behind it.
//
// The array wins whenever it is one, **including when it is empty**: a row
// can carry an inert singular field beside a live list, and an entry whose
// last receipt was removed must read as none rather than resurrecting the
// singular.
export function inlineAttachmentsOf(entry) {
  const inline = inlineOf(entry);
  return coreSync()
    .expenseInlineAttachments(inline.list.map(attachmentIn), inline.hasList, inline.single ? attachmentIn(inline.single) : null)
    .map(pickedFrom([], inline));
}

// The paths alone, which is what the sweep compares. Skips an attachment with
// no path: one can only arrive from a document, and a card with nothing behind
// it is not an object anything can delete.
//
// Takes either shape — a list of `expenseAttachments` rows or an entry document
// with inline fields — since its callers hold one or the other. A row has no
// `attachments` key and an entry has no `path`, so the two cannot be confused.
export function attachmentPaths(source) {
  const list = Array.isArray(source) ? source : inlineAttachmentsOf(source);
  return coreSync().expenseAttachmentPaths(list.map(attachmentIn));
}

/// The two fields an expense document carried its receipts in, before #183.
///
/// Exported so that `planExpenseAttachmentMigration` below (what to read off
/// an old document) and `scripts/migrate-expense-attachments.mjs` (which field
/// paths to delete) share one list.
///
/// Nothing in `src/data/` writes either of them, and `validExpenseEntry` in
/// firestore.rules does not name them, so they can only be on a document
/// written before #183.
export const INLINE_ATTACHMENT_FIELDS = Object.freeze(['attachments', 'attachment']);

/// What `scripts/migrate-expense-attachments.mjs` would do, as a value (#183).
///
/// Pure, and separate from the script, as `planFamilyContactMigration` is: the
/// judgement — what counts as already moved, what has nothing to move, which
/// documents still carry a retired field — is pinned by a test, and the script
/// is REST calls over it.
///
/// Three outcomes per entry, plus `strips` independently:
///
///   * **`creates`** — this entry names receipts inline that have no
///     `expenseAttachments` row yet. One new row per path.
///   * **`already`** — every path it names inline already has a row. A second
///     run after a half-applied first one lands here, which is what makes this
///     safe to run twice.
///   * **`strips`** — the entry document still carries `attachments` or
///     `attachment`, whichever of the two above applies. This is the half that
///     makes a *third* run propose nothing at all.
///
/// An entry that names nothing inline appears in none of the three, because an
/// absent field is not something to strip.
///
/// **Create then strip, per entry**, in that order: stripping first loses the
/// reference if the create then fails, while a failed strip leaves the receipt
/// recorded twice, which `attachmentsFor` resolves in the collection's favour.
///
/// **`addedAt` is the entry's `createdAt`, not now**, so the cards keep their
/// order rather than the order the script walked them. Within one entry the
/// inline list's order is kept by appending an index.
export function planExpenseAttachmentMigration({ entries = [], attachments = [] } = {}) {
  const creates = [];
  const already = [];
  const strips = [];

  const byEntry = new Map();
  for (const row of attachments) {
    if (!row?.entryId) continue;
    if (!byEntry.has(row.entryId)) byEntry.set(row.entryId, new Set());
    byEntry.get(row.entryId).add(row.path);
  }

  for (const entry of entries) {
    if (!entry?.id) continue;
    const fields = INLINE_ATTACHMENT_FIELDS.filter(
      (k) => Object.prototype.hasOwnProperty.call(entry, k) && entry[k] != null,
    );
    const inline = inlineAttachmentsOf(entry).filter((a) => a?.path);
    if (!fields.length && !inline.length) continue;
    if (fields.length) strips.push({ entryId: entry.id, fields });

    const have = byEntry.get(entry.id) || new Set();
    let moved = 0;
    inline.forEach((attachment, index) => {
      if (have.has(attachment.path)) return;
      creates.push({
        entryId: entry.id,
        index,
        attachment: normaliseExpenseAttachment({
          ...attachment,
          entryId: entry.id,
          // `${createdAt}#${index}` keeps the list's order inside one entry
          // without needing a second field: the strings sort the way the
          // indices do, and every row on one entry shares the prefix.
          addedAt: `${entry.createdAt || ''}#${String(index).padStart(3, '0')}`,
        }),
      });
      moved += 1;
    });
    if (inline.length && moved === 0) already.push({ entryId: entry.id, paths: inline.length });
  }

  return {
    creates,
    already,
    strips,
    counts: {
      entries: entries.length,
      creates: creates.length,
      already: already.length,
      strips: strips.length,
    },
  };
}

// An interval firestore.rules will take, from whatever the number input gave.
//
// The clamp — `Math.max(1, Math.round(Number(interval) || 1))` — because a
// zero or negative stride expands to nothing or walks backwards. And finite:
// `<input type="number">` accepts `1e400`, which is `Infinity` after `Number`
// and `Math.round`, and Firestore stores a non-finite number as a double, so
// `interval is int` would refuse the save as a permission error (#115).
export function storableInterval(value) {
  return coreSync().expenseStorableInterval(value == null ? null : String(value));
}

// Free text as the document will store it: trimmed of nothing, cut to what the
// rules will take.
//
// **Only for text this app did not get from a form field.** Where a person
// types, a `maxLength` stops the character being entered rather than
// discarding it afterwards (#93). The receipt scanner fills `payee` and
// `category` from a service's reply, where a `maxLength` does nothing.
export function clampText(value, max) {
  return coreSync().expenseClampText(String(value == null ? '' : value), max);
}

// Deterministic, so two offline devices seeding at once write the same eleven
// documents instead of twenty-two.
export function defaultCategoryId(type, label) {
  return coreSync().expenseDefaultCategoryId(String(type), String(label));
}

const defaultRecord = (r) => ({
  type: r.entryType, label: r.label, id: r.id, isDefault: true, isSystem: r.isSystem,
});

export function defaultCategories() {
  return coreSync().expenseDefaultCategories().map(defaultRecord);
}

// Every category of a type, whichever mode the studio is in.
const storedCategoryOf = (c) => ({
  id: String(c?.id ?? ''),
  entryType: text(c?.type),
  label: text(c?.label),
  isDefault: Boolean(c?.isDefault),
  isSystem: Boolean(c?.isSystem),
});

export function categoryRecords(stored = [], seeded = false) {
  return coreSync()
    .expenseCategoryRecords(stored.map(storedCategoryOf), Boolean(seeded))
    .map((r) => (r.stored == null ? defaultRecord(r) : stored[r.stored]));
}

export function categoriesFor(type, stored = [], seeded = false) {
  return coreSync().expenseCategoriesFor(text(type), stored.map(storedCategoryOf), Boolean(seeded));
}

// The category a family payment is filed under on a cash basis
// (`expenses::SYSTEM_CATEGORY` in the core, `revenueLines`). Named here too
// because Revenue & Expenses' Categories picker (#601) has to offer it even
// for a studio that deleted "Payments from Families" from its own category
// list — a cash-basis report keeps filing family payments there regardless.
export const SYSTEM_CATEGORY = 'Payments from Families';

// Revenue & Expenses' Categories picker (#601): every category the studio
// has configured, for either entry type, plus SYSTEM_CATEGORY — deduped.
// What MMS's picker lists is not known (#601).
export function revenueExpenseCategories(stored = [], seeded = false) {
  const names = [...categoriesFor('expense', stored, seeded), ...categoriesFor('revenue', stored, seeded)];
  if (!names.includes(SYSTEM_CATEGORY)) names.push(SYSTEM_CATEGORY);
  return [...new Set(names)];
}

// Revenue & Expenses' Payees picker (#601): every payee already on an
// expense or other-revenue entry, plus every family with a last name, since
// a family payment is filed under the family's name. There is no configured
// list of payees, so this is built from what is booked (#601).
export function revenueExpensePayees(entries = [], familyLastNames = []) {
  const names = entries.map((e) => (text(e?.payee) || '').trim()).filter(Boolean);
  for (const n of familyLastNames) {
    const trimmed = (text(n) || '').trim();
    if (trimmed) names.push(trimmed);
  }
  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

// ── Two expense categories may not share a name (#144) ──────────────────────
//
// As the calendar's categories refuse a duplicate (#129), so does this list,
// and here it matters more: an expense entry stores its category **by
// label**. `renameCategory` and `removeCategory` in data/useExpenses.jsx both
// select entries with `e.type === type && e.category === <label>`, so the
// label is the join key. Without the refusal:
//
//   * two categories of one name give a rename a set of rows it half-owns, and
//     the predicate cannot tell them apart;
//   * where the two differ only by case, renaming "rent" leaves "Rent"'s rows
//     behind under a name the list no longer contains;
//   * **renaming "Travel" to "Rent" when a "Rent" already exists would
//     relabel every Travel entry to "Rent"**, with nothing to undo it.
//
// So `renameCategory` is refused exactly as `addCategory` is, and the check
// takes the draft's own id so renaming a category to the name it already has is
// not a clash with itself. No merge ("move every Travel entry into Rent") is
// offered; it would need its own control and a confirmation naming the row
// count.
//
// ── What "the same name" is, and where it is scoped ─────────────────────────
//
// `categoryKey` from domain/categories.js, shared with #129's editor, #110's
// backfill and #134's import: case, surrounding whitespace, internal runs of
// whitespace and Unicode composition fold; a typo does not. That includes the
// one axis where the key knowingly diverges from MMS: "Group  Lesson" beside
// "Group Lesson" is refused here and accepted there (see `categoryErrors`).
//
// **Scoped to the type.** The stock list ships "Other" on both sides, and
// `categoryRecords` above keys on `${type}:${label}`; comparing across types
// would refuse the studio's own seed batch. Every predicate that reads the
// label is already `e.type === type && e.category === …`.
//
// ── MyMusicStaff ───────────────────────────────────────────────────────────
//
// MMS's calendar categories refuse a duplicate with an inline message under
// the Name field, Save enabled throughout, folding case and surrounding
// whitespace and quoting the existing category's own spelling (#129). Whether
// its Expenses & Other Revenue → Categories screen does the same is not known;
// this applies #129's shape and `duplicateLabelMessage`'s wording, because of
// the rename hazard above.
const recordOf = (c) => ({
  id: String(c?.id ?? ''),
  entryType: String(c?.type ?? ''),
  label: String(c?.label ?? ''),
  isDefault: Boolean(c?.isDefault),
  isSystem: Boolean(c?.isSystem),
  stored: null,
});

export function expenseCategoryErrors(draft, records = []) {
  const refusal = coreSync().expenseCategoryError(
    text(draft?.id),
    text(draft?.type),
    typeof draft?.label === 'string' ? draft.label : null,
    records.filter(Boolean).map(recordOf),
  );
  // Not a check for an empty name, as in `categoryErrors`: an empty name greys
  // Save out instead.
  return refusal ? { label: refusal.message } : {};
}

// Two fields, and the boolean is the one this believes — see ledger.js's
// isActive (#70). Imported as well as re-exported: a bare `export … from`
// creates no local binding.
export { isActive };

// A refund flips the sign: an expense refunded is money coming back, and revenue
// refunded is money going out. Storing it as a flag rather than a negative
// amount keeps the entry readable — "$40 refund" rather than "-$40".
export function signedMinor(entry) {
  return coreSync().expenseSignedMinor(entryOf(entry));
}

export function entriesBetween(entries, startISO, endISO) {
  return entries.filter((e) => {
    const d = e.occurredOn || '';
    return d >= startISO && d <= endISO;
  });
}

// A recurring entry is stored once, as a rule, and its instances are derived —
// the same discipline lessons use, for the same reason: writing twelve rows for
// a monthly rent means twelve documents two offline devices can disagree about.
//
// Without the expansion a recurring expense would count only in the month it
// started, so "Rent, monthly, $400" would show up once and every later month
// look $400 cheaper than it was.
export function expandEntry(entry, startISO, endISO) {
  const dates = coreSync().expenseEntryDates(entryOf(entry), String(startISO), String(endISO));
  if (entry.recurrence !== 'recurring') return dates.length ? [entry] : [];
  return dates.map((date) => ({
    ...entry,
    occurredOn: date,
    instanceOf: entry.id,
    // The rule's anchor, kept alongside the instance date so an editor can put
    // the series back where it started instead of re-anchoring it on whichever
    // month was on screen.
    seriesStart: entry.occurredOn,
  }));
}

// Expense/revenue instances in a window, recurrence expanded. Distinct from
// `entriesBetween`, which stays a plain date filter because the ledger it also
// serves has no recurrence rules.
const instanceOf = (entries) => (i) => (i.series
  ? { ...entries[i.index], occurredOn: i.occurredOn, instanceOf: entries[i.index].id, seriesStart: entries[i.index].occurredOn }
  : entries[i.index]);

export function expenseInstancesBetween(entries, startISO, endISO) {
  return coreSync()
    .expenseInstances(entries.map(entryOf), String(startISO), String(endISO))
    .map(instanceOf(entries));
}

// The same expansion, plus which rules ran into the cap.
//
// An indefinite rule expands to fill whatever window it is handed, so a wide
// date range — or a mistyped year — turns one monthly rent into a thousand rows
// cut off at an arbitrary point. Truncated output that looks complete is the
// failure mode worth refusing: the caller gets the ids so it can say so.
export function expenseInstancesReport(entries, startISO, endISO) {
  const crossing = entries.map(entryOf);
  const instances = coreSync()
    .expenseInstances(crossing, String(startISO), String(endISO))
    .map(instanceOf(entries));
  const truncated = coreSync().expenseTruncatedRules(crossing, String(startISO), String(endISO));
  return { instances, truncated };
}

export function monthBounds(yearMonth) {
  const { start, end } = coreSync().expenseMonthBounds(String(yearMonth));
  return { start, end };
}

// The summary block: Teaching, Other Revenue, Expenses, Net Income.
//
// `basis` decides which ledger events count as teaching income, and it is not a
// presentation toggle — it changes what the number means. Cash counts money when
// it arrives (payments); accrual counts it when it is earned (charges). A studio
// files taxes on one basis or the other, and showing the wrong one is worse than
// showing nothing.
export function financialSummary({ ledgerEntries, expenseEntries, yearMonth, basis = 'accrual' }) {
  const s = coreSync().expenseFinancialSummary(
    ledgerEntries.map(ledgerOf), expenseEntries.map(entryOf), String(yearMonth), String(basis),
  );
  return {
    teachingMinor: s.teachingMinor,
    otherRevenueMinor: s.otherRevenueMinor,
    expensesMinor: s.expensesMinor,
    netIncomeMinor: s.netIncomeMinor,
  };
}

export function addMonths(yearMonth, delta) {
  return coreSync().expenseAddMonths(String(yearMonth), Number(delta));
}

// The trend chart's window. MMS does not show the last N months ending at the
// selection — it centres on it, six months back and three forward, and draws
// the forward tail dashed. That tail is a forecast, not history: scheduled
// lessons that have not been taught and recurring costs that have not been
// paid. Keeping it in the same chart is the point — a teacher deciding whether
// to take on a student is asking about next month, not last.
//
// `projectTeaching(ym)` supplies forecast teaching income for a future month.
// It is injected rather than computed here because it needs the calendar and
// student rates, and this module stays free of everything but its arguments so
// it can run unchanged inside a Cloud Function.
export function monthlySeries({
  ledgerEntries,
  expenseEntries,
  yearMonth,
  basis = 'accrual',
  before = 6,
  after = 3,
  currentMonth = null,
  projectTeaching = null,
}) {
  // Anything after the real calendar month is forecast — not anything after the
  // *selected* month. Stepping the picker back to March must not redraw April
  // through today as a projection; those months already happened.
  const months = coreSync().expenseSeriesMonths(String(yearMonth), before, after, text(currentMonth));
  // A future month has no ledger yet, so teaching income comes from the
  // schedule. Expenses and other revenue already project correctly: recurring
  // rules expand into future windows on their own.
  const teaching = months.map((m) => (m.projected && projectTeaching ? Number(projectTeaching(m.yearMonth)) || 0 : 0));
  return coreSync()
    .expenseMonthlySeries(
      ledgerEntries.map(ledgerOf), expenseEntries.map(entryOf), String(yearMonth), String(basis),
      before, after, text(currentMonth), teaching,
    )
    .map((p) => ({
      yearMonth: p.yearMonth,
      projected: p.projected,
      teachingMinor: p.teachingMinor,
      otherRevenueMinor: p.otherRevenueMinor,
      expensesMinor: p.expensesMinor,
      netIncomeMinor: p.netIncomeMinor,
    }));
}

// Money the way an accountant reads it: a negative wrapped in parentheses
// rather than carrying a minus sign. MMS renders Net Income this way and it is
// the convention on every statement a teacher will have seen.
export function formatAccounting(minor, formatter) {
  const n = Number(minor) || 0;
  return coreSync().expenseAccountingText(n, formatter(Math.abs(n)));
}

// How many entries reference each category, for the manager's Transactions
// column. Counted on the stored rules, not on expanded instances: a monthly
// rent is one transaction the teacher entered, not twelve.
export function categoryUsage(entries) {
  return new Map(coreSync()
    .expenseCategoryUsage(entries.map(entryOf))
    .map((u) => [`${u.entryType}:${u.category}`, u.count]));
}

// Category totals for a period, largest first.
export function categoryBreakdown(expenseEntries, startISO, endISO, type = 'expense') {
  return coreSync()
    .expenseCategoryBreakdown(expenseEntries.map(entryOf), String(startISO), String(endISO), String(type))
    .map(({ category, totalMinor }) => ({ category, totalMinor }));
}

// ── What the provider writes ───────────────────────────────────────────────
//
// Here rather than in `src/data/useExpenses.jsx`, so the phones write the same
// documents: an entry, an edit's merge, an imported row, the category seed,
// a new category, a rename's and a delete's writes, a receipt row, and the
// reconciliation an edit makes of an entry's receipts. The provider keeps the
// batches, the ids it mints (`freeId`) and the bucket; what each write *is*
// lives here. `serverTimestamp` is handed in, as `documentFromRows` takes it.

// The prefix a new entry's `freeId` carries.
export function entryIdPrefix(type) {
  return coreSync().expenseEntryIdPrefix(text(type));
}

// A new entry, as `addEntry` writes it.
//
// `amountMinor` is the backstop, not the refusal (#166). `firestore.rules`
// requires `amountMinor >= 0`, so this clamps, as `storableInterval` does for
// `interval`. The refusal with a sentence is `entryErrors` in
// src/domain/expenseForms.js, so only a caller with no form in front of it
// reaches this with a negative.
//
// Recurrence is stored on the entry; expansion reuses domain/recurrence so a
// repeating expense behaves like a repeating lesson. `interval` is clamped and
// made finite — a number input will hand back "1e400", and
// `interval is int` in firestore.rules refuses a non-finite number (#145).
// `endDate` is where the rule stops, or null for "repeat indefinitely". Both
// halves of the tombstone are written live.
const entryInputOf = (e) => ({
  entryType: text(e?.type),
  payee: text(e?.payee),
  category: text(e?.category),
  amountMinor: Number(e?.amountMinor),
  isRefund: Boolean(e?.isRefund),
  description: text(e?.description),
  occurredOn: text(e?.occurredOn),
  recurrence: text(e?.recurrence),
  frequency: text(e?.frequency),
  interval: text(e?.interval),
  repeatIndefinitely: e?.repeatIndefinitely === undefined ? null : Boolean(e.repeatIndefinitely),
  endDate: text(e?.endDate),
});

export function expenseEntryDocument(input, serverTimestamp) {
  return documentFromRows(coreSync().expenseEntryDocument(entryInputOf(input)), serverTimestamp);
}

// An edit, as `updateEntry` merges it onto the entry. The same backstops as a
// new entry. Editing an instance of a recurring rule edits the rule: the
// instances are derived and have no documents of their own.
export function expenseEntryUpdateDocument(fields) {
  return documentFromRows(coreSync().expenseEntryUpdateDocument(entryInputOf(fields)));
}

// One imported row, as `importEntries` writes it: a one-time entry, never a
// refund — the import has no column for either.
export function importedEntryDocument(draft, serverTimestamp) {
  return documentFromRows(coreSync().expenseImportedEntryDocument(entryInputOf(draft)), serverTimestamp);
}

// The writes that turn the built-in categories into real documents, so the
// manager can rename and delete them like any other: the eleven, then the
// marker that says the studio has been seeded. Deterministic ids: two devices
// doing this offline at the same time converge on eleven rows.
export function categorySeedWrites(serverTimestamp) {
  return coreSync().expenseCategorySeedIds().map((id) => ({
    id,
    document: documentFromRows(coreSync().expenseCategorySeedDocument(id), serverTimestamp),
  }));
}

// A new category, or null for a blank name. The name is written trimmed, and
// the trimmed name is what the "+ Add Category" drawer then files the entry
// under.
//
// **No duplicate check here.** The refusal lives in the two editors —
// `expenseCategoryErrors` above, wired into pages/ExpenseCategoriesPage.jsx and
// the "+ Add Category" drawer on pages/ExpensesPage.jsx (#144) — where it can
// be said to the person who typed the name.
export function newCategoryDocument({ type, label }, serverTimestamp) {
  const rows = coreSync().expenseNewCategoryDocument(text(type), String(label));
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

// What a rename writes, or null when there is nothing to do (a blank name, or
// the name it already has): the category's new label, and the same label onto
// every entry of its type filed under the old one.
//
// Renaming carries the entries with it: the category is stored on each entry
// by label. So the name it is renamed *to* is refused if the studio already
// has it — see `expenseCategoryErrors`.
const filedOf = (e) => ({ id: String(e?.id ?? ''), entryType: text(e?.type), category: text(e?.category) });

export function categoryRenamePlan({ type, from, to }, entries) {
  const label = coreSync().expenseCategoryRename(String(from), String(to)) ?? null;
  if (label == null) return null;
  return {
    document: documentFromRows(coreSync().expenseCategoryLabelDocument(label)),
    entryIds: coreSync().expenseCategoryEntries(text(type), String(from), entries.map(filedOf)),
    entryDocument: documentFromRows(coreSync().expenseEntryCategoryDocument(label)),
  };
}

// What a delete writes besides removing the category: every entry of its type
// filed under it falls back to "Not Specified", which is a state the summaries
// already handle, instead of vanishing from the books.
//
// `refusal` is set for the category family payments file into —
// flagged `isSystem`, or the revenue category of that name — and a caller
// writes nothing then.
export function categoryRemovalPlan({ type, label, isSystem = false }, entries) {
  return {
    refusal: coreSync().expenseCategoryRemovalRefusal(Boolean(isSystem), text(type), String(label)) ?? null,
    entryIds: coreSync().expenseCategoryEntries(text(type), String(label), entries.map(filedOf)),
    entryDocument: documentFromRows(coreSync().expenseEntryCategoryDocument('')),
  };
}

// The Mileage Log's `expenseCategory` setting (Business Settings → Mileage
// Log) names a category by label, the same join key an entry's `category`
// is — so a rename or a delete that moves the entries but leaves this one
// setting behind points it at a label with no category behind it (#555).
// These two read the plan a rename or a delete should also carry, decided in
// the core (`mileage_category_after_rename`, `mileage_category_cleared_by_removal`)
// so the web and both phones agree on when the setting follows.

// The label to patch `mileage.expenseCategory` to, or null for nothing to do.
export function mileageCategoryAfterRename({ type, from, to }, storedCategory) {
  return coreSync().mileageCategoryAfterRename(text(type) ?? 'expense', text(storedCategory), String(from), String(to)) ?? null;
}

// Whether a delete of `{ type, label }` clears `mileage.expenseCategory`.
export function mileageCategoryClearedByRemoval({ type, label }, storedCategory) {
  return coreSync().mileageCategoryClearedByRemoval(text(type) ?? 'expense', text(storedCategory), String(label));
}

// The rows one entry's receipts should end up as (#104, #183).
//
// The backstop, not the refusal, as `storableInterval` is. Rebuilt rather than
// spread by `normaliseExpenseAttachment`, so a field picked up somewhere in the
// form cannot ride along into the write.
//
// The count is cut silently. `attachRefusal` in src/domain/expenseForms.js is
// the refusal with a sentence; this stands behind it for a caller with no form
// in front of it.
//
// `addedAt` is carried through when the form already has one — an attachment
// being re-saved on an edit keeps the instant it was attached, so editing a
// payee does not reshuffle the cards. `now` stamps the ones that have none.
export function storableAttachments(list, entryId, now = new Date().toISOString()) {
  const given = Array.isArray(list);
  return coreSync()
    .expenseStorableAttachments(given ? list.filter(Boolean).map(attachmentIn) : [], given, String(entryId), String(now))
    .map(attachmentOut);
}

// A receipt row as written: the attachment and its `createdAt`.
export function attachmentRowDocument(attachment, serverTimestamp) {
  return documentFromRows(coreSync().expenseAttachmentDocument({
    id: '',
    entryId: String(attachment.entryId ?? ''),
    path: String(attachment.path ?? ''),
    name: String(attachment.name ?? ''),
    contentType: String(attachment.contentType ?? ''),
    size: Number(attachment.size) || 0,
    addedAt: String(attachment.addedAt ?? ''),
  }), serverTimestamp);
}

// What an edit does to an entry's receipts (#183): the inline fields to strip
// off the entry, the rows to delete, and the rows to create with their ids.
//
// The form holds the list the entry should end up with, removals included, and
// this turns that into the smallest set of writes that produces it: a delete
// for every row whose path the form no longer names, a create for every path
// that has no row yet, and nothing at all for a path that is on both sides.
//
// **Keyed on `path`, not on the row id.** An attachment's path is minted at
// upload and is unique per object, so it is the only identity the form and the
// collection share — the form has never carried a document id, because the
// object is uploaded before any row exists to name it.
//
// The id is *derived from* that path (`attachmentIdForPath`), which is what
// makes this reconciliation safe against a window that has not caught up: a
// create it makes because `existing` looked empty is a write of the same
// content to the same document rather than a second row for the same receipt.
//
// Untouched rather than re-set for a path on both sides, which is what keeps
// `addedAt` — and therefore the card order — stable across an edit that only
// changed the payee.
//
// ── An edit migrates the entry it is editing ───────────────────────────────
//
// Leaving a legacy row's inline `attachments` / `attachment` in place would let
// the two records disagree, and the migration would believe the wrong one:
//
//   1. A pre-#183 entry carries `attachments: [A, B]` and has no rows.
//   2. A teacher detaches B and saves. `existing` is empty, so nothing is
//      deleted and a row is created for A. `attachmentsFor` prefers the
//      collection the moment it has a row, so the screen is right: one receipt.
//   3. The inline list still says `[A, B]`.
//   4. `planExpenseAttachmentMigration` treats inline as authoritative and
//      creates a row for every inline path with no row — so it **creates B**,
//      and the receipt the teacher detached is back.
//
// So the invariant: **an entry never carries inline attachment fields and
// `expenseAttachments` rows at the same time.** Stripping them in the same
// batch as the edit is cheap and atomic.
//
// The strip is conditional twice over: on the entry still carrying them, so a
// converted entry is not re-written on every save — and on **`attachments`
// actually being a list**. `storableAttachments` reads a missing value as *no
// receipts*, so a caller that omitted the key would otherwise retire the only
// record of this entry's receipts.
//
// `queued` is the row ids this device has written and not yet read
// back: they count as rows that exist, so a second Save does not create them
// again with a fresh `createdAt` the rules refuse — and the batch with it.
export function attachmentReconciliation({
  attachments, entryId, rows = [], entry = null, now = new Date().toISOString(), queued = [],
}) {
  const given = Array.isArray(attachments);
  const args = [
    given ? attachments.filter(Boolean).map(attachmentIn) : [],
    given,
    String(entryId),
    rows.map(attachmentIn),
    Boolean(entry && entry.attachments != null),
    Boolean(entry && entry.attachment != null),
    String(now),
  ];
  return {
    strip: coreSync().expenseReceiptsToStrip(...args),
    deletes: coreSync().expenseReceiptsToDelete(...args),
    creates: coreSync().expenseReceiptsToCreate(...args, queued.map(String))
      .map((a) => ({ id: a.id, attachment: attachmentOut(a) })),
  };
}

// What deleting an entry takes with it: its receipt rows, in the same batch as
// the entry — a row whose `entryId` names nothing is unreachable from every
// screen, and one batch cannot half-apply — and every object either record
// names, to take out of the bucket once the batch has landed.
//
// **Both places a receipt can be recorded (#183).** The live one is an
// `expenseAttachments` row; the inline list and the singular `attachment` are
// on entries written before it. A delete that knew only one would leave the
// other's objects charged against the studio's ceiling with nothing naming
// them.
const receiptArgs = (rows, entryId, entry) => {
  const inline = inlineOf(entry);
  return [
    rows.map(attachmentIn),
    entryId == null ? null : String(entryId),
    inline.list.map(attachmentIn),
    inline.hasList,
    inline.single ? attachmentIn(inline.single) : null,
  ];
};

export function entryRemovalPlan(entryId, rows = [], entry = null) {
  const args = receiptArgs(rows, entryId, entry);
  return {
    rowIds: coreSync().expenseRemovalRowIds(...args),
    paths: coreSync().expenseRemovalPaths(...args),
  };
}

// The receipts on one entry, in the order they were attached (#183).
//
// **Falls back to the entry's inline attachments** for a row the migration has
// not reached, whose collection is empty. The collection wins whenever it has a
// row, so a migrated entry never shows the stale inline copy beside the live
// one.
export function attachmentsFor(rows, entries, entryId) {
  const entry = entries.find((e) => e.id === entryId);
  return coreSync()
    .expenseAttachmentsFor(...receiptArgs(rows, entryId, entry))
    .map(pickedFrom(rows, inlineOf(entry)));
}
